import { ChangeEvent, ComponentProps, InputHTMLAttributes, MouseEvent, MouseEventHandler, ReactNode, useCallback, useRef } from 'react';

import { Button } from '../../../Button';
import { ImageUploadState } from '../ImageUpload.state';

import { ImageUploadContainer } from './Container';

interface ImageUploadInputProps<T> extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'onChange' | 'onClick'> {
  readonly size?: ComponentProps<typeof Button>['size'];
  readonly children?: ReactNode;
  readonly onAdd: ImageUploadState<T>['onAdd'];
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
  readonly testId?: string;
}

function ImageUploadInput<T>({
  className,
  size = 'none',
  children = size === 'none' ? 'Upload File' : 'Upload Images',
  accept = 'image/png, image/jpeg',
  onAdd,
  onClick,
  multiple,
  testId,
  ...inputProps
}: ImageUploadInputProps<T>) {
  const input = useRef<HTMLInputElement>(null);

  const onClickButton = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      input.current?.click();
      onClick?.(e);
    },
    [onClick],
  );

  const onFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        onAdd(e.target.files);
      }
    },
    [onAdd],
  );

  return (
    <ImageUploadContainer dashed className={className}>
      <input
        ref={input}
        data-testid={testId ?? 'image-upload'}
        {...inputProps}
        accept={accept}
        type="file"
        multiple={multiple}
        onChange={onFileChange}
        className="absolute left-0 top-0 h-full w-full opacity-0"
      />
      <Button
        variant="secondary"
        size={size}
        className={(size === 'none' && 'rounded text-xs px-1 py-0.5') || undefined}
        onClick={onClickButton}
      >
        {children}
      </Button>
    </ImageUploadContainer>
  );
}

export { ImageUploadInput };
